import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import { UserContext } from '../../../../App'
import API from '../../../../services/API'

// Referral programme panel — lives on the Billing page because the reward is
// account credit. There is no trial for the referred organisation (AmbuNet
// has no trials since the demo tenant replaced them); the only benefit is the
// £200 credit to the referrer after the referred organisation's first paid
// invoice (see server/src/services/referralProgramme.js).

const STATUS_COLOURS = {
  Pending: 'default',
  'Awaiting First Payment': 'warning',
  Successful: 'success',
  'Credit Issued': 'success',
  Ineligible: 'error',
  Cancelled: 'default',
}

const rules = [
  'Referral codes must belong to an active, paying AmbuNet customer.',
  'The code must be entered on the sign-up form when the new organisation subscribes — it cannot be added afterwards.',
  'The organisation must be a genuinely new AmbuNet prospect and must not already subscribe to, or have previously subscribed to, AmbuNet.',
  'The organisation must not already be in an active sales discussion, have booked or completed a demonstration, received a quotation, or begun onboarding.',
  'Self-referrals, or referrals between connected organisations made mainly to obtain credit, may be declined.',
  'AmbuNet will make the final decision on whether a referral meets the programme rules.',
]

const formatDate = (value) => (value ? moment(value).format('DD MMM YYYY') : '—')
const formatMoney = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
  }).format(Number(value || 0))

export default function ReferralProgramme() {
  const { user } = useContext(UserContext)
  const isSiteAdmin = user?.security?.isSiteAdmin === true
  const [programme, setProgramme] = useState(null)
  const [activity, setActivity] = useState(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState('')

  useEffect(() => {
    let active = true
    const load = async () => {
      const programmeResponse = await API.get('/referrals/programme', {
        skipGlobalErrorHandler: true,
      })
      if (!active) return
      if (programmeResponse.status !== 200) {
        setError('Referral programme information is temporarily unavailable.')
        return
      }
      setProgramme(programmeResponse.data)

      if (isSiteAdmin) {
        const activityResponse = await API.get('/referrals/admin', {
          skipGlobalErrorHandler: true,
        })
        if (!active) return
        if (activityResponse.status === 200) {
          setActivity(activityResponse.data)
        } else {
          setActivity({ error: true })
          setError('Referral statistics could not be loaded right now.')
        }
      }
    }
    load().catch(() => {
      if (active) setError('Referral programme information is temporarily unavailable.')
    })
    return () => {
      active = false
    }
  }, [isSiteAdmin])

  const copy = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(type)
      window.setTimeout(() => setCopied(''), 2500)
    } catch (_error) {
      setError('Your browser could not copy that text. Please select and copy it manually.')
    }
  }

  if (!programme && !error) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 5 }}>
        <Skeleton width="35%" height={42} />
        <Skeleton />
        <Skeleton width="70%" />
      </Paper>
    )
  }

  if (!programme) return <Alert severity="error">{error}</Alert>

  const displayCode = programme.referralCode.toUpperCase()
  const rewardAmount = formatMoney(programme.rewardAmount ?? 200)

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.25, md: 3 },
        borderRadius: 5,
        border: '1px solid rgba(35,154,164,0.2)',
        background: 'linear-gradient(145deg, #fff, rgba(35,154,164,0.045))',
        boxShadow: '0 18px 60px rgba(15,23,42,0.08)',
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center" mb={1}>
            <GroupAddRoundedIcon color="contrast" />
            <Typography variant="h5" fontWeight={950} color="anBlue.main">
              Refer an organisation
            </Typography>
          </Stack>
          <Typography color="text.secondary">
            Know another ambulance or medical provider that could benefit from
            AmbuNet? Share your organisation’s referral code with them — they
            enter it on the sign-up form when they subscribe.
          </Typography>
        </Box>

        {error && <Alert severity="warning">{error}</Alert>}

        <Box>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={800}>
                  Your organisation’s code
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={0.5}>
                  <Typography
                    variant="h4"
                    fontWeight={950}
                    sx={{ letterSpacing: '0.08em', flex: 1, wordBreak: 'break-word' }}
                  >
                    {displayCode}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={copied === 'code' ? <CheckCircleRoundedIcon /> : <ContentCopyRoundedIcon />}
                    onClick={() => copy(programme.referralCode, 'code')}
                  >
                    {copied === 'code' ? 'Copied' : 'Copy code'}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7} display="flex">
              <Benefit icon={<CardGiftcardRoundedIcon />}>
                Your organisation earns {rewardAmount} AmbuNet account credit
                after the referred organisation’s first successful subscription
                payment. The credit is applied to your AmbuNet billing account
                and has no cash value.
              </Benefit>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography fontWeight={900} mb={1}>A few important details</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
            {rules.map((rule) => (
              <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.75 }}>
                {rule}
              </Typography>
            ))}
          </Box>
        </Box>

        {isSiteAdmin && <AdminReferralActivity data={activity} />}
      </Stack>
    </Paper>
  )
}

function Benefit({ icon, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, flex: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box color="contrast.main">{icon}</Box>
        <Typography variant="body2">{children}</Typography>
      </Stack>
    </Paper>
  )
}

function AdminReferralActivity({ data }) {
  if (!data) {
    return (
      <Box>
        <Skeleton width="30%" height={36} />
        <Skeleton height={90} />
      </Box>
    )
  }

  if (data.error) {
    return (
      <Alert severity="error">
        Referral statistics and activity are temporarily unavailable.
      </Alert>
    )
  }

  const cards = [
    ['Total referrals', data.summary.totalReferrals],
    ['Pending', data.summary.pendingReferrals],
    ['Successful', data.summary.successfulReferrals],
    ['Declined or ineligible', data.summary.declinedReferrals],
    ['Total credit earned', formatMoney(data.summary.totalCreditEarned)],
  ]
  if (data.summary.availableAccountCredit !== null) {
    cards.push(['Available account credit', formatMoney(data.summary.availableAccountCredit)])
  }

  return (
    <Box sx={{ pt: 3, borderTop: '1px solid rgba(15,23,42,0.1)' }}>
      <Typography variant="h6" fontWeight={950} mb={2}>Referral statistics</Typography>
      <Grid container spacing={1.5} mb={2.5}>
        {cards.map(([label, value]) => (
          <Grid item xs={6} md key={label}>
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={950}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography fontWeight={900} mb={1}>Referral activity</Typography>
      {!data.activity.length ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography fontWeight={800}>No referrals yet</Typography>
          <Typography variant="body2" color="text.secondary">
            New referral activity will appear here after a valid code is used at signup.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Signed up</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Subscribed</TableCell>
                <TableCell>Credit</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.activity.map((row) => (
                <TableRow key={row.id || row._id} hover>
                  <TableCell>{row.referredOrganisationName || 'Pending signup'}</TableCell>
                  <TableCell>{row.referralCode?.toUpperCase()}</TableCell>
                  <TableCell>{formatDate(row.submittedAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={STATUS_COLOURS[row.status] || 'default'} />
                  </TableCell>
                  <TableCell>{formatDate(row.subscriptionDate)}</TableCell>
                  <TableCell>{formatMoney(row.creditAmount)}</TableCell>
                  <TableCell>{formatDate(row.creditIssuedDate)}</TableCell>
                  <TableCell>{row.ineligibilityReason || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
